/* eslint-disable array-bracket-newline */
/**
 * This hook delivers information about:
 * a) Pool (actually Ticker)
 * b) Coin
 * Than it merges it into one payload
 */
import { useState, useEffect } from 'react';
import { ICurrentState } from '../../../reducers/current';
import { KattanaAPI } from '../../../store/createStore';

const REFRESH_TIME = 30000;

export interface IToken {
  id: number;
  title: string;
  slug: string;
  coingecko_id: string;
  coinmarketcap_id: string;
  trustwallet_id: string;
  logo_url: string;
  website: string;
  long_title: string;
  telegram: string;
  twitter_id: string;
  reddit: string;
  github: string;
}

const useDexPoolInfo = (currentState: ICurrentState) => {
  const { chain, base_address, quote_address, flipped } = currentState;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IToken | null>(null);

  const [base, quote] = currentState.name.split('/');

  const poolData = {
    'Pool created': currentState.pool_created,
    'Pool liquidity': currentState.liquidity,
    [`Pooled ${base}`]: currentState.base_reserve,
    [`Pooled ${quote}`]: currentState.quote_reserve,
    'Daily volume': currentState.volume,
  };

  if (currentState.annual_percentage_yield) {
    poolData['APY'] = currentState.annual_percentage_yield;
  }

  const fetchData = (
    chain: string,
    address: string,
    controller: AbortController,
    refresh: boolean
  ) => {
    if (chain && address) {
      if (!refresh) {
        setLoading(true);
      }
      KattanaAPI
        .tokenInfo(chain, address, { signal: controller.signal })
        .then((res: IToken | null) => {
          setData(res);
        })
        .catch(() => {
          setData(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  /**
   * Fetch aggregated data
   */
  useEffect(() => {
    const controller = new AbortController();
    const address = flipped ? quote_address : base_address;

    fetchData(chain, address, controller, false);

    const interval = setInterval(() => {
      fetchData(chain, address, controller, true);
    }, REFRESH_TIME);

    return () => {
      clearInterval(interval);
      controller.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, base_address, quote_address]);

  return {
    loading,
    data,
    poolData,
  };
};

export default useDexPoolInfo;
